import React from "react";
import { Plus, ShoppingCart, Wallet, Clock } from "lucide-react";
import Button from "../ui/Button";
import { formatDate } from "../../utils/date";

// Once the welcome-credit deadline is this close, the quiet "expires on <date>"
// line escalates to a day-count and picks up warning styling.
const WELCOME_CREDIT_URGENCY_DAYS = 3;

// welcomeCredits: { expires_at, remaining, expired } | null (see useEntitlements).
// Renders nothing when there's nothing worth warning about: no welcome grant,
// nothing left to lose, or the deadline has already passed (silence beats
// stale "expiring soon" copy once it's actually gone).
function welcomeCreditsNotice(welcomeCredits) {
  if (!welcomeCredits || welcomeCredits.expired) return null;
  const remaining = Number(welcomeCredits.remaining || 0);
  if (remaining <= 0) return null;

  // Grants made before the 14-day policy existed carry no expiry, and the backend
  // reports expires_at: null for them. They genuinely never expire — the expiry job
  // skips them, and those users were told at signup that their credits don't expire.
  //
  // This must be checked explicitly: new Date(null) is the epoch, not an invalid
  // date, so the arithmetic below produced a huge negative daysLeft, tripped the
  // urgency branch, and told those users their credits "expire today".
  const expiresAtMs = welcomeCredits.expires_at
    ? new Date(welcomeCredits.expires_at).getTime()
    : NaN;
  if (Number.isNaN(expiresAtMs)) return null;

  const daysLeft = Math.ceil((expiresAtMs - Date.now()) / (1000 * 60 * 60 * 24));
  const urgent = daysLeft <= WELCOME_CREDIT_URGENCY_DAYS;
  const credits = remaining.toLocaleString();

  if (urgent) {
    const dayLabel = daysLeft <= 0 ? "today" : daysLeft === 1 ? "in 1 day" : `in ${daysLeft} days`;
    return { urgent, text: `${credits} free signup credits expire ${dayLabel}` };
  }
  return { urgent, text: `${credits} free signup credits expire on ${formatDate(welcomeCredits.expires_at)}` };
}

function WelcomeCreditsNotice({ welcomeCredits }) {
  const notice = welcomeCreditsNotice(welcomeCredits);
  if (!notice) return null;
  return (
    <p
      className={`text-xs mt-1 flex items-center gap-1 ${
        notice.urgent ? "text-amber-600 font-medium" : "text-muted-foreground"
      }`}
    >
      <Clock className="h-3 w-3 flex-shrink-0" />
      {notice.text}
    </p>
  );
}

export default function CreditsCard({ summary, onBuyCredits, prominent = false, welcomeCredits = null }) {
  // Display math is anchored to reality: `used` is what was actually debited
  // from the plan bucket this period, `left` is what's currently in the bucket.
  // Denominator = used + left, so the X/Y counter can never contradict the
  // "N left" readout — even across rollover, trial, mid-cycle plan swaps, or a
  // scheduled downgrade where `sub.plan_id` got overwritten prematurely.
  const planLeft = Number(summary.plan_balance || 0);
  const topupLeft = Number(summary.topup_balance || 0);
  const totalBalance = Number(summary.total_balance || 0);
  const planUsed = Number(
    summary.plan_credits_used_in_period ??
      Math.max(
        0,
        Number(summary.plan_period_allocation ?? summary.subscription?.monthly_credits ?? 0) -
          planLeft
      )
  );
  const periodAllocation = planUsed + planLeft;
  const planUsedPct = periodAllocation > 0
    ? Math.min(100, Math.round((planUsed / periodAllocation) * 100))
    : 0;
  const planName = summary.subscription?.plan_name || "Plan";

  // PAYG burn-rate hint — only when a phone rate is actually priced.
  const phoneRate = Number(summary.pricing?.simulation_phone || 0);
  // Requires a POSITIVE balance, not just a rate. Pay-as-you-go is allowed to settle
  // as low as -10 so work already admitted can finish, and dividing that through gave
  // "≈ -2 phone-test minutes left" — a negative quantity of a thing you cannot have a
  // negative quantity of, shown right under an already-alarming balance.
  const phoneMinutesLeft = phoneRate > 0 && totalBalance > 0 ? Math.floor(totalBalance / phoneRate) : null;

  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden h-full">
      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100 h-full">
        {/* ── Column 1 ── */}
        {prominent ? (
          // PAYG: no plan allocation — lead with the total balance as the hero.
          <div className="p-4 flex flex-col justify-center gap-1 min-h-[180px]">
            <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">
              <Wallet className="h-3 w-3" />
              Credit balance
            </div>
            <div className="text-4xl font-semibold text-foreground tabular-nums leading-tight">
              {totalBalance.toLocaleString()}
              <span className="text-base font-normal text-muted-foreground"> credits</span>
            </div>
            {phoneMinutesLeft != null && (
              <p className="text-xs text-muted-foreground mt-1">
                ≈ {phoneMinutesLeft.toLocaleString()} phone-test minutes left
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-1">Credits you buy never expire</p>
            <WelcomeCreditsNotice welcomeCredits={welcomeCredits} />
          </div>
        ) : (
          <div className="p-4 flex flex-col gap-5 min-h-[180px]">
            {/* Plan credits */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">
                  {planName} credits this period
                </div>
                <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">
                  Total: <span className="text-foreground tabular-nums">{totalBalance.toLocaleString()}</span>
                </span>
              </div>
              <div className="flex items-baseline justify-between gap-2">
                <div className="text-2xl font-semibold text-foreground tabular-nums leading-tight">
                  {planUsed.toLocaleString()}
                  <span className="text-sm font-normal text-muted-foreground">
                    {" "}/ {periodAllocation ? periodAllocation.toLocaleString() : "—"}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground tabular-nums">
                  <span className="font-medium text-foreground">{planLeft.toLocaleString()}</span> left
                </span>
              </div>
              <WelcomeCreditsNotice welcomeCredits={welcomeCredits} />
              {periodAllocation > 0 && (
                <div className="mt-2">
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        planUsedPct >= 80 ? "bg-red-500" : planUsedPct >= 50 ? "bg-amber-500" : "bg-primary"
                      }`}
                      style={{ width: planUsedPct + "%" }}
                    />
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">Resets each billing period</div>
                </div>
              )}
            </div>

            {/* Top-up reserve — separate progress / wallet */}
            <div className="pt-4 border-t border-gray-50">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">
                  <Wallet className="h-3 w-3" />
                  Top-up reserve
                </div>
                <span className="text-[10px] text-muted-foreground">Used after plan runs out</span>
              </div>
              <div className="flex items-baseline justify-between gap-2">
                <div className="text-2xl font-semibold text-foreground tabular-nums leading-tight">
                  {topupLeft.toLocaleString()}
                  <span className="text-sm font-normal text-muted-foreground"> credits</span>
                </div>
                <span className="text-xs text-muted-foreground">Purchased credits never expire</span>
              </div>
              {topupLeft > 0 && (
                <div className="mt-2">
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-emerald-500 w-full" />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Column 2: buy extra credits ── */}
        <div className="p-4 bg-gradient-to-br from-gray-50/50 via-white to-gray-50/50 flex flex-col justify-between min-h-[180px]">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-7 h-7 rounded-lg bg-card border border-border shadow-sm flex items-center justify-center">
                <ShoppingCart className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
              <div className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">
                Need more?
              </div>
            </div>
            <div className="text-sm font-medium text-foreground mb-1">Buy extra credits</div>
            <div className="text-xs text-muted-foreground leading-relaxed">
              {prominent
                ? "Credits you buy never expire. Pay only for the test runs and evaluations you actually use."
                : "Top-up credits never expire. They are used only after your plan credits for the period are exhausted."}
            </div>
          </div>

          <div className="mt-4">
            <Button variant="primary" onClick={onBuyCredits} className="w-full justify-center">
              <Plus className="h-4 w-4" />
              <span>{prominent ? "Add credits" : "Buy credits"}</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
